/**
 * Rules behind an IPO application.
 *
 * Every value here is transcribed from the user platform's own IPO journey
 * (`smest-next/components/IPO/SelectSeries.tsx` and `PaymentSteps.tsx`), which
 * is the reference implementation for `/ipo/ipo_ordercreate/`. An admin placing
 * an order on a client's behalf must apply the same rules, or the exchange will
 * reject the bid — or worse, accept a wrong one.
 *
 * THE TWO UUID LISTS AND THE SPECIAL FACE VALUE ARE FRONTEND-ONLY OVERRIDES.
 * They are not served by any API; they live in the user platform as hardcoded
 * constants and are mirrored here. When the user platform's lists change, these
 * must change with them.
 */

/** BSE category codes. The payload sends the quote's `category_subcode`. */
export const RETAIL_CATEGORY = 4;
export const HNI_CATEGORY = 3;

/**
 * Anything above this is HNI (category 3); at or below it is Retail
 * (category 4). The user platform switches the investor between tabs on this
 * boundary; here it is a validation the admin cannot silently cross.
 */
export const RETAIL_MAX_INVESTMENT = 200000;

/** Default per-series unit bounds. */
export const DEFAULT_MIN_UNITS = 10;
export const DEFAULT_MAX_UNITS = 500;

/**
 * One IPO prices its series 3 at a face value the security master does not
 * carry, with its own unit bounds. Frontend-only override — see the file note.
 */
export const SPECIAL_FACE_VALUE_IPO_ID = "199189b0-66cc-44d3-947a-185723a50b60";
export const SPECIAL_FACE_VALUE_SERIES = 3;
export const SPECIAL_FACE_VALUE = 100000;
export const SPECIAL_SERIES_MIN_UNITS = 1;
export const SPECIAL_SERIES_MAX_UNITS = 5;

/**
 * IPOs whose Retail (category 4) tab the user platform disables, so every
 * application goes in as HNI regardless of size.
 */
export const CATEGORY_4_DISABLED_IPO_IDS = [
  "ab8ef5d9-5dc6-49fe-bfec-4f68d87c00a9",
  "b5b114da-2c1a-49f3-935b-b18910315961",
  "419a1ca5-b09b-4e14-8fdd-f532ff601044",
  "96bf2418-0025-48b8-92b1-91a47352fad2",
  "a90f0799-259f-4f09-8443-a400e070a966",
  "5b7a63ed-7fcd-4f3a-b210-2cb9a9cd5452",
  "225f51f1-4a74-4aa0-8fb9-f95ed6536d2c",
  "ac0df45f-8b8b-4963-9b05-b8b86464533a",
  "7b77f6de-d50e-4183-8091-f8aedc8cec90",
  "26063ebe-a2be-44b0-ad48-ca0cf7e59449",
  "cd060e1a-8cf1-4dfd-8ee1-cfa2ccd9a981",
];

/**
 * Fixed values the exchange expects on a UPI application. `bankname` 8888 and
 * `location` UPIIDL are the UPI sponsor codes; they are constants in the user
 * platform too, not anything derived from the investor's bank.
 */
export const UPI_BANK_NAME = "8888";
export const UPI_LOCATION = "UPIIDL";

/** "N" = new bid. The modify/cancel codes are not offered on this screen. */
export const ACTION_CODE_NEW = "N";
export const BID_TYPE = "1";
export const PAY_TYPE = "1";

/**
 * UPI handles the ASBA sponsor bank accepts, transcribed verbatim from
 * `smest-next/helpers/static_data.ts` (`permittedUPIApps[].handle`). Several
 * rows list more than one handle in a single string, exactly as they do there.
 *
 * An application against an unlisted handle is rejected at the exchange, so
 * this is checked before the order is created rather than after.
 */
export const PERMITTED_UPI_HANDLE_SOURCE = [
  "@BARODAMPAY",
  "@abfspay",
  "@idbi",
  "@upi",
  "@aubank",
  "@bandhan",
  "@dlb",
  "@indus",
  "@kbl",
  "@federal",
  "@pnb",
  "@sbi",
  "@uco",
  "@citi & @citigold",
  "@dlb",
  "@dbs",
  "@inhdfc",
  "@freecharge",
  "@okhdfcbank, @okaxis, @oksbi, @okicici",
  "@yesg",
  "@hsbc",
  "@idbi",
  "@icici",
  "@indianbank, @allbank",
  "@indie",
  "@kotak",
  "@ikwik",
  "@naviaxis",
  "@unionbankofindia, @uboi and @unionbank",
  "@ptsbi",
  "@ptaxis",
  "@ptyes",
  "@pthdfc",
  "@pz",
  "@ybl, @axl, @ibl",
  "@shriramhdfcbank",
  "@sib",
  "@yespay",
];

/**
 * The individual handles, split out of the rows above.
 *
 * ONE DELIBERATE DIVERGENCE FROM THE USER PLATFORM. It splits those rows on
 * ", " only, which leaves "@citi & @citigold" and
 * "@unionbankofindia, @uboi and @unionbank" partly stuck together — so an
 * investor on @citi, @citigold, @uboi or @unionbank is told their handle is not
 * permitted even though the source data lists it. Splitting on "&" and "and"
 * as well fixes that. The list is the source of truth; how it was punctuated
 * is not.
 */
export const PERMITTED_UPI_HANDLES: string[] = Array.from(
  new Set(
    PERMITTED_UPI_HANDLE_SOURCE.reduce<string[]>(
      (handles, row) =>
        handles.concat(
          row
            // Word boundaries around "and" matter: a bare /and/ would
            // split "@bandhan" into "@b" and "han".
            .split(/,|&|\band\b/)
            .map((handle) => handle.trim().toLowerCase())
            .filter((handle) => handle.indexOf("@") === 0)
        ),
      []
    )
  )
);

/**
 * The shape of a UPI id, checked before the handle is looked up above. Same
 * pattern the user platform validates with.
 */
export const UPI_ID_PATTERN = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;

export const DEPOSITORY_OPTIONS = ["NSDL", "CDSL"];
